import { Alert, AlertTitle } from '@folio/components';
import { parseWakamError } from '@folio/utils/parseWakamError';
import { type QuoteError, quoteFailedUserHelp } from '@folio/utils/quoteFailedHelp';
import { getLatestAsset } from '@folio/utils/useCurrentActiveAsset';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQuery } from '@tanstack/react-query';
import {
  keyValueFieldsToObject,
  policyControllerCalculatePolicyQuoteQueryOptions,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import _ from 'lodash';
import { AlertOctagonIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';

const SUPPORTED_PRODUCTS = ['axeceVtc', 'axeceMultiRisquesPro'];

export function PolicyWakamErrorBanner() {
  const { t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();
  // Broker-only, but folio renders product banners on the policyholder dashboard too.
  const isEnabled =
    useMatch('/policyholder/*') === null && SUPPORTED_PRODUCTS.includes(policy.productId);

  const { data: existingAsset } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policy?.id, {
      branchId: workingBranch?.id,
    }),
    select: data =>
      getLatestAsset(
        data.data.map(asset => ({
          ...asset,
          customFields: keyValueFieldsToObject(asset.customFields ?? []),
        })),
      ),
    enabled: isEnabled,
  });

  const quoteParams =
    policy.productId === 'axeceVtc'
      ? {
          assetIds: [existingAsset?.id!],
          options: [
            {
              assetId: existingAsset?.id!,
              tier: 'BASIC',
              excess: 'DEFAULT',
              perils: [],
            },
          ],
          customFieldsToOverride: [
            { key: 'oldDrivequantScoreRange', value: '[8;8.5[' },
            { key: 'newDrivequantScoreRange', value: '[8;8.5[' },
            { key: 'managementFeeTaxesIncluded', value: 0 },
          ],
          brokerageFeesOverride: [],
        }
      : {
          assetIds: [existingAsset?.id!],
          options: [
            {
              assetId: existingAsset?.id!,
              tier: 'BASIC',
              excess: 'DEFAULT',
              perils: [],
            },
          ],
          customFieldsToOverride: [],
          brokerageFeesOverride: [],
        };

  const { error } = useQuery({
    ...policyControllerCalculatePolicyQuoteQueryOptions(policy?.id, quoteParams, {
      branchId: workingBranch?.id,
    }),
    enabled: !!policy?.brokerageFirm && !!existingAsset?.id && !!workingBranch?.id && isEnabled,
    retry: false,
  });

  const quoteErrors = _.get(error, 'errorResponse.data.details.quoteErrors') as
    | QuoteError[]
    | undefined;

  const fallback = (_.get(error, 'errorResponse.data.details.failedReason') ??
    _.get(error, 'errorResponse.data.details.errorMessage')) as string | undefined;

  const wakamError = quoteErrors?.length
    ? quoteFailedUserHelp(quoteErrors)
    : (parseWakamError(fallback) ?? undefined);

  if (!wakamError) {
    return null;
  }

  return (
    <Alert variant="destructive" className="flex justify-between items-center">
      <div>
        <AlertTitle className="flex items-center gap-4 pt-1">
          <AlertOctagonIcon />
          {t('errors.wakamPricingError')}
        </AlertTitle>
        {/* Fix Wakam typo on the fly */}
        {wakamError?.replace('avecc', 'avec')}
      </div>
    </Alert>
  );
}
